// Third party libraries
import { RecommendApp } from "@channelwill/components"
import { useMount } from "@channelwill/hooks"
import { CWILL_EVENT_NAMES } from "@channelwill/report"
import { useSmartVisibleCrossSellTracker } from "@packages/polling-checker/index"
// Shopify imports
import { isShopifyEmbedded } from "@shopify/app-bridge-utils"
import {
  Banner,
  Button,
  Card,
  Divider,
  Grid,
  Icon,
  Layout,
  LegacyCard,
  Link,
  ProgressBar,
  Text,
  Tooltip,
} from "@shopify/polaris"
import { ChatIcon, EditIcon, IconsFilledIcon, PersonIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import dayjs from "dayjs"
import { Observer, observer } from "mobx-react"
import React, { useCallback, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useIntercom } from "react-use-intercom"

// Internal imports
import { AppPricingInterval } from "@/api/account/type.d"
import api from "@/api/common"
import { RecommendAppI } from "@/api/common/types/RecommendApps"
import dashboard from "@/api/dashboard"
import { TMSMigration } from "@/api/dashboard/type"
import { Container, KlaviyoMigrationReminderBanner } from "@/component"
import CustomPage from "@/component/CustomPage/CustomPage"
import DatePicker from "@/component/DatePicker"
import { ImageBanner } from "@/component/ImageBanner/ImageBanner"
import PPIFeeWavierBanner from "@/component/PPIFeeWavierBanner"
import { LINK_BOOK_A_DEMO } from "@/consts/link"
import { CROSS_SELL_POINT_ENUM } from "@/enums/CrossSellPoint"
import Permission from "@/enums/permission"
import { BannerEnum } from "@/models/IUserInfo"
import AnalyticCard from "@/pages/Analytics/components/AnalyticCard"
import AnalyticInfos from "@/pages/Analytics/components/AnalyticInfos"
import AnalyticsPaidUpgrade from "@/pages/Analytics/components/PaidUpgrade/PaidUpgrade"
import { ChineseWechatMarketingBanner } from "@/pages/Dashboard/components"
import AppBlocksStatus from "@/pages/Dashboard/components/AppBlocksStatus"
import BookADemo202506Banner from "@/pages/Dashboard/components/BookADemo202506Banner"
import { ContactGroup } from "@/pages/Dashboard/components/ContactGroup"
import CrossSellBanner from "@/pages/Dashboard/components/CrossSellBanner"
import MarketingCarousel from "@/pages/Dashboard/components/MarketingCarousel"
import MigrateSetupGuide from "@/pages/Dashboard/components/MigrateSetupGuide/MigrateSetupGuide"
import OrderStatusList from "@/pages/Dashboard/components/OrderStatusList"
import QuickSetupGuide from "@/pages/Dashboard/components/QuickSetupGuide"
import SetupGuideSkeleton from "@/pages/Dashboard/components/QuickSetupGuide/SetupGuideSkeleton"
import { RecommendedCard } from "@/pages/Dashboard/components/RecommendedCard"
import RecommendedModule from "@/pages/Dashboard/components/RecommendedModule"
import SpecialOffer from "@/pages/Dashboard/components/SpecialOffer"
import StaffingCost from "@/pages/Dashboard/components/ValueCalculation/StaffingCost"
import StaffingTime from "@/pages/Dashboard/components/ValueCalculation/StaffingTime"
import { TableDatePickerOnChangeHandle } from "@/pages/Dashboard/Events"
import HelpCenterCard from "@/pages/GetHelp/components/HelpCenterCard"
import stores from "@/stores"
import { currencyFormat, formatNumber, percentNumber } from "@/utils/analyticsUtils"
import toast from "@/utils/toast"

import { QuotaNoticeBanner, ShowAccountPlanErr } from "./components/Banners"
import styles from "./Dashboard.module.scss"
import DashboardInit from "./init"


// 抽离渲染货币格式化函数
const renderCurrency = (value: string | number, format: string, defaultValue = "-") => {
  // @ts-ignore
  return <>{currencyFormat(typeof value === "number" ? String(value) : value, format, defaultValue)}</>
}

// 渲染配额计数函数
const renderQuotaCount = (count: number): React.ReactNode => {
  const { t } = useTranslation("dashboard")

  return t("QuotaAvailable", { count: formatNumber(count) })
}



/**
 * 仪表盘主组件
 */
const Dashboard = observer(() => {
  const { t } = useTranslation("dashboard")
  const { t: commonT } = useTranslation("common")
  const navigate = useNavigate()

  // 状态管理
  const [staffingTimeModal, setStaffingTimeModal] = useState<boolean>(false)
  const [staffingCostModal, setStaffingCostModal] = useState<boolean>(false)
  const [quickSetupSwitching, setQuickSetupSwitching] = useState<boolean>(false)
  const [popoverActive, setPopoverActive] = useState<boolean>(false)
  const [recommendedApps, setRecommendedApps] = useState<RecommendAppI[]>([])
  const [showTMSIntergration, setShowTMSIntergration] = useState<boolean>(false)
  const [tmsMigration, setTmsMigration] = useState<TMSMigration | null>(null)

  // Store数据获取 - 使用解构赋值
  const { userInfoSore, EventsStore, shipmentsLookupsDataStore, commonStore } = stores

  // 快速设置指南切换处理
  const handleQuickSetupChange = useCallback((): void => {
    setQuickSetupSwitching(true)
    setTimeout(() => setQuickSetupSwitching(false), 300)
  }, [])

  // 日期选择器应用处理
  const handleDatePickerApply = useCallback((selected: number, startDate: Date, endDate: Date): void => {
    TableDatePickerOnChangeHandle(selected, startDate, endDate)
    EventsStore.setStartTime(dayjs(startDate).format("YYYY-MM-DD"))
    EventsStore.setEndTime(dayjs(endDate).format("YYYY-MM-DD"))
    EventsStore.setSelected(String(selected))
  }, [EventsStore])

  // 人力时间保存处理
  const handleStaffingTimeSave = useCallback((value: string): void => {
    shipmentsLookupsDataStore.saveValueCoefficient({ save_time: value }).then(() => {
      toast(commonT("SavedSuccess"))
      setStaffingTimeModal(false)
    })
  }, [shipmentsLookupsDataStore, commonT])

  // useEffect(() => {
  //   dashboard.resetTmsMigrationDismiss()
  // }, [])

  useMount(() => {
    // 初始化仪表盘数据
    DashboardInit.init()

    // 重置日期范围
    EventsStore.setStartTime(void 0)
    EventsStore.setEndTime(void 0)
    EventsStore.setSelected("60")

    try {
      // 访问首页埋点
      if (!window.isFromAdmin && isShopifyEmbedded()) {
        window.cio && window.cio.trigger({
          event: "view_home_page",
        })
        window.cwreport && window.cwreport.report(CWILL_EVENT_NAMES.VIEW_HOME_PAGE)
      }
    } catch (e) {
      console.log(e)
    }
    // 用于重置弹窗，非测试时勿取消注释
    // commonApi.reopenPopup(PopupsEnum.BOOK_A_DEMO_POPUP)
  })

  useEffect(() => {
    // 获取推荐应用 hello 列表
    api.getRecommendAppsHello().then(({ data }) => {
      setRecommendedApps(data.recommend_apps_hello)
      commonStore.setRecommendAppsPartnersData(data.recommend_apps_partners)
    })

    dashboard.getTmsMigration().then(({ data, status }) => {
      if (status === 204){
        setShowTMSIntergration(false)
      } else if (status === 200){
        setShowTMSIntergration(true)
        setTmsMigration(data?.tms_migrations ?? null)
      }
    })
  }, [])

  const renderDashboard = () => {
    const { showNewMessage } = useIntercom()

    const {
      shipments_count,
      lookups_count,
      delivery_performance,
      valid_tracking,
      getStaffingTime,
      getStaffingCost,
      getCurrency,
      as_value_coefficient,
      save_loading,
      conversion_revenue,
      shipmentStatus,
      shipment_status_chart_data,
    } = shipmentsLookupsDataStore

    const {
      data: {
        remaining_order_count,
        total_order_count,
        current_subscription,
        plan,
        plan_interval,
        discount,
        discount_start,
        is_legacy_klaviyo_connected,
        show_account_tip,
        is_in_cn,
        cross_sell_customer_language,
        currency,
        money_with_currency_format,
      },
      local,
      isFreeUser,
      isOldUser,
      oldPayingUsers,
      showCrossSellBanner,
      quickSetupInitialized,
      setLocalVar,
      setDataKey,
      setDiscountSingle,
      closeBannerById,
    } = userInfoSore

    const { showFreeAdd } = local
    const { showQuotaNotice, showFreeOrderAdd } = userInfoSore
    const showPaidUpgrade = isFreeUser || (isOldUser && !oldPayingUsers)

    const staffingCost =
      getCurrency() === "USD"
        ? Number(getStaffingCost).toFixed(2)
        : window.Currency
          ? window.Currency.convert(
            getStaffingCost,
            as_value_coefficient.cost_monetary_unit,
            getCurrency(),
          ).toFixed(2)
          : 0

    let conversionRevenueTotal = Number(conversion_revenue.total)

    if (as_value_coefficient.cost_monetary_unit === currency) {
      conversionRevenueTotal = Number((conversionRevenueTotal + Number(staffingCost)).toFixed(2))
    }
    if (showPaidUpgrade) {
      conversionRevenueTotal = Number(staffingCost)
    }

    // 显示额度提醒
    const showQuotaRemain = userInfoSore.local.showQuotaRemain && userInfoSore.showQuotaNotice

    // 已使用额度；超额使用时剩余额度可能为负，按 total 封顶
    const totalOrderCount = total_order_count || 0
    const usedOrderCount = Math.min(Math.max(totalOrderCount - remaining_order_count, 0), totalOrderCount)

    const { ref: expertSupportRef } = useSmartVisibleCrossSellTracker({
      onViewed: () => {
        window.CrossSellTracker?.logViewed(CROSS_SELL_POINT_ENUM.HelpCenterContact)
      },
      threshold: 0.3, // 30%可见时触发
    })

    return  <Container id="Page-Dashboard">
      <Layout>
        <Layout.Section>
          <CustomPage
            title={
              <Trans ns="dashboard" i18nKey="welcome_h1">
                <Link removeUnderline onClick={() => navigate("/integration?selected_tab=9")} />
              </Trans>
            }
            paddingBlockEnd={{ xs: "0", sm: "0", md: "200", lg: "200", xl: "200" }}
            rightChildren={
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div className={classNames("mt-2 sm:mt-0 mb-2 sm:mb-0 flex flex-col", styles.datePicker)}>
                  <Tooltip
                    content={renderQuotaCount(remaining_order_count)}
                    preferredPosition="below"
                    width="wide"
                    hasUnderline
                  >
                    <Text variant="bodySm" fontWeight="semibold" as="span">
                      {t("UsedTotal")}: {formatNumber(usedOrderCount)}
                      {" / "}
                      {formatNumber(totalOrderCount)}
                    </Text>
                  </Tooltip>

                  <div className="mt-[2px]">
                    <ProgressBar
                      progress={totalOrderCount ? usedOrderCount / totalOrderCount * 100 : 0}
                      size="small"
                      tone={showQuotaRemain ? "critical" : "highlight"}
                    />
                  </div>
                </div>

                <DatePicker
                  preferredAlignment="right"
                  buttonProps={{ variant: "secondary" }}
                  onApply={handleDatePickerApply}
                />

                <SpecialOffer />
              </div>
            }
          />
        </Layout.Section>

        {/* Klaviyo迁移提醒横幅 */}
        {is_legacy_klaviyo_connected && (
          <Layout.Section>
            <KlaviyoMigrationReminderBanner />
          </Layout.Section>
        )}

        {/* 营销轮播图 */}
        <Layout.Section>
          <PPIFeeWavierBanner entry="Dashboard" />
          <MarketingCarousel />
        </Layout.Section>

        {/* 额度不足提醒横幅 */}
        {showQuotaRemain && (
          <Layout.Section>
            <QuotaNoticeBanner
              isNewPlanVersion={current_subscription?.version >= 3}
              consumeOrderCount={remaining_order_count}
              onDismiss={() => setLocalVar("showQuotaRemain", false)}
              planPrice={plan}
              isAnnually={plan_interval === AppPricingInterval.Annual}
            />
          </Layout.Section>
        )}

        {/* 折扣横幅 */}
        <Layout.Section>
          {discount.banner_id && discount_start && (
            <ImageBanner
              id={discount.banner_id}
              show={!discount.banner_displayed}
              onClose={() => setDiscountSingle("banner_displayed", true)}
              to="/billing/plans"
              src={discount.banner_img}
              style={{ paddingBottom: 0 }}
            />
          )}
        </Layout.Section>

        {/* 账户套餐异常提示 */}
        <Layout.Section>
          {show_account_tip && (
            <ShowAccountPlanErr
              onDismiss={() => setDataKey("show_account_tip", false)}
            />
          )}

          {/* 中国微信营销横幅 */}
          {is_in_cn && <ChineseWechatMarketingBanner />}
        </Layout.Section>

        {/* 仪表盘主要内容 */}
        <Layout.Section>
          {showTMSIntergration && (
            <MigrateSetupGuide
              initialTmsMigration={tmsMigration}
              onDismiss={() => setShowTMSIntergration(false)}
            />
          )}

          {/* 快速设置指南 */}
          {quickSetupInitialized
            ? <QuickSetupGuide onChangeStep={handleQuickSetupChange} />
            : <SetupGuideSkeleton />
          }

          {/* 交叉销售横幅 */}
          {showCrossSellBanner && (
            <div className="mb-4">
              <CrossSellBanner
                isChina={cross_sell_customer_language === "Chinese"}
                onClose={() => closeBannerById(BannerEnum.CrossSale)}
              />
            </div>
          )}

          {/* Book Demo Banner */}
          {(() => {
            const bookDemoBanner = userInfoSore.getBookDemoBanner

            return userInfoSore.data.should_show_book_a_demo_guide && bookDemoBanner?.display === true && (
              <div className="mb-4">
                <BookADemo202506Banner
                  imageUrl={bookDemoBanner.image_url}
                  onClose={() => userInfoSore.closeBannerById(BannerEnum.BOOK_A_DEMO_2025_06)}
                />
              </div>
            )
          })()}

          {/* 分析卡片网格 */}
          <div className="mb-4">
            <Grid gap={{ xs: "1rem", sm: "1rem", md: "1rem", lg: "1rem", xl: "1rem" }}>
              <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <AnalyticCard
                  disabledTooltip={quickSetupSwitching}
                  title={t("ShipmentsLookups.title")}
                  description={t("ShipmentsLookupsDesc")}
                  amount={`${formatNumber(shipments_count)} / ${formatNumber(lookups_count)}`}
                >
                  <AnalyticInfos
                    disabledTooltip={quickSetupSwitching}
                    className={"mt-3"}
                    infos={[
                      {
                        title: t("ShipmentsLookups.deliveryPerformance"),
                        description: t("ShipmentsLookups.deliveryPerformanceDesc"),
                        amount: delivery_performance.value,
                        render(value) {
                          if (value === 0) return "-"
                          return <>
                            {formatNumber(value)}{" "}
                            <Text as={"span"}
                              tone={"subdued"}
                            >({percentNumber(delivery_performance.percentage || "-")})</Text>
                          </>
                        },
                      },
                      {
                        title: t("ShipmentsLookups.validTracking"),
                        description: t("ShipmentsLookups.validTrackingDesc"),
                        amount: valid_tracking.value,
                        render(value) {
                          if (value === 0) return "-"
                          return <>
                            {formatNumber(value)}{" "}
                            {
                              !valid_tracking.percentage ? ""
                                : <Text as={"span"} tone={"subdued"}>
                                  ({!value ? "-" : percentNumber(valid_tracking.percentage || "-")})
                                </Text>
                            }
                          </>
                        },
                      },
                      {
                        title: t("ShipmentsLookups.savedStaffingTime"),
                        description: t("ShipmentsLookups.savedStaffingTimeDesc"),
                        icon: EditIcon,
                        iconClick: () => setStaffingTimeModal(true),
                        amount: getStaffingTime
                          ? t("workingHours", { hours: getStaffingTime })
                          : "-",
                      },
                    ]}
                  />
                </AnalyticCard>
              </Grid.Cell>
              <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <div className={styles.cardFull}>
                  <AnalyticCard
                    title={t("TotalConversionRevenue")}
                    description={t("TotalConversionRevenueDesc")}
                    amount={currencyFormat(String(conversionRevenueTotal), showPaidUpgrade ? window.Currency && window.Currency.rates[currency] ? money_with_currency_format : "${{amount}} USD" : money_with_currency_format, "-")}
                  >
                    {showPaidUpgrade ? (
                      <div className="mt-3">
                        <AnalyticsPaidUpgrade
                          lines={3}
                          primaryButton
                          hiddenButton
                          permission={Permission.ProductRecommendationAndDiscountUpsell}
                          subButton={
                            <Trans ns="dashboard" i18nKey="upgradeTips">
                              <Button
                                onClick={() => {
                                  window.open("https://docs.parcelpanel.com/shopify/getting-started/increasing-sales-revenue/")
                                }}
                                variant="plain"
                              >
                                  Tip
                              </Button>
                            </Trans>
                          }
                        />
                        <AnalyticInfos
                          className="mt-5"
                          infos={[
                            {
                              title: t("SavedStaffingCost"),
                              description: t("SavedStaffingCostDesc"),
                              icon: EditIcon,
                              iconClick: () => setStaffingCostModal(true),
                              amount: staffingCost || "-",
                              render(value) {
                                return value === "-"
                                  ? value
                                  : currencyFormat(
                                      value as string,
                                      window.Currency && window.Currency.rates[currency]
                                        ? money_with_currency_format
                                        : "${{amount}} USD",
                                      "-",
                                  )
                              },
                            },
                          ]}
                        />
                      </div>
                    ) : (
                      <AnalyticInfos
                        className="mt-3"
                        infos={[
                          {
                            title: t("TrackingPage"),
                            description: t("TrackingPageDesc"),
                            amount: conversion_revenue.tracking_page,
                            render: (value) => renderCurrency(value, money_with_currency_format),
                          },
                          {
                            title: t("ShippingNotifications"),
                            description: t("ShippingNotificationsDesc"),
                            amount: conversion_revenue.shipping_notifications,
                            render: (value) => renderCurrency(value, money_with_currency_format),
                          },
                          {
                            title: t("SavedStaffingCost"),
                            description: t("SavedStaffingCostDesc"),
                            icon: EditIcon,
                            iconClick: () => setStaffingCostModal(true),
                            amount: staffingCost || "-",
                            render(value) {
                              if (value === "-") return value
                              const formatConfig = window.Currency && window.Currency.rates[currency]
                                ? money_with_currency_format
                                : "${{amount}} USD"

                              return renderCurrency(value, formatConfig)
                            },
                          },
                        ]}
                      />
                    )}
                  </AnalyticCard>
                </div>
              </Grid.Cell>
            </Grid>
          </div>

          <StaffingTime
            open={staffingTimeModal}
            loading={save_loading}
            initValue={as_value_coefficient.save_time}
            onClose={() => setStaffingTimeModal(false)}
            onSave={handleStaffingTimeSave}
          />

          <StaffingCost
            open={staffingCostModal}
            loading={save_loading}
            prefix={getCurrency()}
            inputLoading={false}
            initValue={as_value_coefficient.save_cost}
            onClose={() => setStaffingCostModal(false)}
            onSave={(value) => {
              shipmentsLookupsDataStore.saveValueCoefficient({ save_cost: value }).then(() => {
                toast(commonT("SavedSuccess"))
                setStaffingCostModal(false)
              })
            }}
          />

          <div className="mb-4">
            <OrderStatusList
              dataset={shipmentStatus}
              chartData={shipment_status_chart_data}
              orderTime={{
                selected: EventsStore.selected,
                orderedAtMin: EventsStore.startTime,
                orderedAtMax: EventsStore.endTime,
              }}
            />
          </div>

          <Observer>
            {() => <RecommendApp protection={+userInfoSore.data.is_pi_eligible} />}
          </Observer>

          {/* 推荐模块 - 只在开发环境和测试环境显示 */}
          {import.meta.env.MODE !== "production" && (
            <div className={"mt-4"}>
              <LegacyCard title={t("Recommended.title")}>
                <div className={"pt-2"}><Divider /></div>
                <RecommendedModule className={"p-4 recommended-module"} />
              </LegacyCard>
            </div>
          )}
          {userInfoSore.isShowBanner(BannerEnum.CARD_DISMISSED_UNLOCK_TOP_APPS) &&
            <div className={"mt-4"}>
              <RecommendedCard recommendedApps={recommendedApps} />
            </div>
          }

          {
            <div className={"mt-4"}>
              <AppBlocksStatus />
            </div>
          }
          {/* Help Center Section */}
          <div className={"mt-4"}>
            {(() => {
              let titleRight = undefined

              if (userInfoSore.data.cross_sell_eligible) {
                titleRight = (
                  <div ref={expertSupportRef as any}>
                    <ContactGroup
                      hasBookDemo
                      CROSS_SELL_POINT={CROSS_SELL_POINT_ENUM.HelpCenterContact}
                      popoverActive={popoverActive}
                      setPopoverActive={setPopoverActive}
                      utm="T-onboarding"
                      children={<Button
                        variant="plain"
                        icon={ChatIcon}
                        disclosure
                        onClick={() => setPopoverActive(!popoverActive)}
                      >
                        {t("GetExpertSupport")}
                      </Button>}
                    />
                  </div>
                )
              } else if (userInfoSore.data.should_show_book_a_demo_guide) {
                titleRight = (<Link
                  target={"_blank"}
                  removeUnderline
                  url={LINK_BOOK_A_DEMO}
                >
                  <span className={"inline-flex hover:underline"}>
                    <Icon
                      source={PersonIcon}
                      tone="inherit"
                    />
                    {t("BookADemo")}
                  </span>
                </Link>)
              }

              return <HelpCenterCard titleRight={titleRight} />
            })()}
          </div>
        </Layout.Section>
      </Layout>
    </Container>
  }

  return (
    <Observer>
      {renderDashboard}
    </Observer>
  )
})

export default Dashboard
